import { TreeView } from "@/components/tree-view";
import { Skeleton } from "@/components/ui/skeleton";
import { EmailCard, EmailCardSkeleton } from "@cohesive/ui-components";
import { ErrorBoundary } from "@sentry/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useDebounce } from "@uidotdev/usehooks";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { BaseSelectorProps } from "../BaseSelector";
import {
  buildLabelTree,
  DEFAULT_FOLDERS_ORDER,
  getMailFolderIcon,
  LabelTreeNode,
  MessageTabConfig,
  TreeDataItem,
} from "./types";

import { buildGenericById, toGenericArray } from "../../genericProjection";
import { useOutlookEmailFoldersList } from "./useOutlookEmailFoldersList";
import { useOutlookEmailMessagesList } from "./useOutlookEmailMessagesList";

import {
  EmailEntity,
  EmailFolder,
  EmailFolderEntity,
} from "@cohesive/api-entities";
import {
  Selector,
  SelectorResults,
  SelectorSearch,
  SelectorSidebar,
} from "../Selector";

export const OutlookSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const { data: foldersResponse, isLoading: isLabelsLoading } =
    useOutlookEmailFoldersList(connection, {});

  const allLabels = useMemo(() => {
    return (foldersResponse?.data || []) as EmailFolderEntity[];
  }, [foldersResponse?.data]);

  // Process labels into system and user groups
  const { systemLabels, userLabels } = useMemo(() => {
    const system: EmailFolder[] = [];
    const user: EmailFolder[] = [];

    allLabels.forEach((label) => {
      // Skip labels that are hidden from the label list
      if (label.data.hidden) {
        return;
      }

      if (label.data.type === "system") {
        system.push(label.data);
      } else {
        user.push(label.data);
      }
    });

    // Sort system labels according to defined order
    system.sort((a, b) => {
      const indexA = DEFAULT_FOLDERS_ORDER.indexOf(a.name);
      const indexB = DEFAULT_FOLDERS_ORDER.indexOf(b.name);
      if (indexA === -1 && indexB === -1) return a.name.localeCompare(b.name);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return { systemLabels: system, userLabels: user };
  }, [allLabels]);

  // Build tree data structure for user labels
  const userLabelTree = useMemo(() => {
    return buildLabelTree(userLabels);
  }, [userLabels]);

  // Generate tree view data for system labels
  const systemTreeItems = useMemo(() => {
    const items: TreeDataItem[] = systemLabels.map((label) => {
      const Icon = getMailFolderIcon(label.name);
      return {
        id: label.id,
        name: label.name
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b(\w)/g, (s) => s.toUpperCase()),
        icon: Icon,
        labelType: "system",
        onClick: () => {
          setSelectedTab({
            type: "message",
            folderId: label.id,
            label: label.name,
            icon: <Icon className="h-4 w-4" />,
          });
        },
      };
    });
    return items;
  }, [systemLabels]);

  // Process user label tree into TreeDataItems
  const processLabelTreeToTreeData = useCallback(
    (node: LabelTreeNode): TreeDataItem => {
      const Icon = getMailFolderIcon(node.type);
      const hasChildren = node.children && node.children.length > 0;

      const treeItem: TreeDataItem = {
        id: node.id,
        name: node.name,
        icon: Icon,
        labelType: "user",
        onClick: () => {
          setSelectedTab({
            type: "message",
            folderId: node.id,
            label: node.fullPath,
            icon: <Icon className="h-4 w-4" />,
          });
        },
      };

      if (hasChildren) {
        treeItem.children = node.children.map((child) =>
          processLabelTreeToTreeData(child),
        );
      }

      return treeItem;
    },
    [],
  );

  // Generate complete tree view data
  const treeData = useMemo(() => {
    const userTreeItems = userLabelTree.map((node) =>
      processLabelTreeToTreeData(node),
    );

    // Combine system labels and user labels into a single flat array
    return [...systemTreeItems, ...userTreeItems];
  }, [systemTreeItems, userLabelTree, processLabelTreeToTreeData]);

  // Set initial selected tab
  const initialTab = useMemo<MessageTabConfig>(() => {
    const AllMailIcon = getMailFolderIcon("INBOX");
    return {
      type: "message",
      folderId: "INBOX",
      label: "INBOX",
      icon: <AllMailIcon className="h-4 w-4" />,
    };
  }, [systemLabels]);

  const [selectedTab, setSelectedTab] = useState<MessageTabConfig>(initialTab);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebounce(searchQuery, 500);
  const parentRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  } = useOutlookEmailMessagesList(connection, {
    query_type: debouncedQuery ? "search" : "list",
    search_query: debouncedQuery ? { text: debouncedQuery } : undefined,
    list_query: !debouncedQuery
      ? { sort_by: "received_date", sort_order: "desc" }
      : undefined,
    folder: selectedTab.folderId,
    limit: 25,
  });

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleRecordSelect = (message: EmailEntity) => {
    onSelect(message);
  };

  // Setup virtualization and transform message data to match api-entities types
  const messages = useMemo(() => {
    const rawMessages =
      data?.pages.flatMap((page) => page.data).filter(Boolean) || [];

    // Transform messages to add 'type' field to recipients
    return rawMessages.map((message: any) => ({
      ...message,
      data: {
        ...message.data,
        recipients:
          message.data.recipients?.map((recipient: any) => ({
            ...recipient,
            type: recipient.type || "to", // Default to "to" if not specified
          })) || [],
      },
    }));
  }, [data?.pages]);

  const messageIds = useMemo(() => messages.map((m) => m.data.id), [messages]);

  // Generic projections (entity_format=both) keyed by entity id — placed on the
  // canvas in place of the typed email message.
  const genericById = useMemo(
    () =>
      buildGenericById(
        data?.pages.flatMap((page) => toGenericArray(page.data_generic)) ?? [],
      ),
    [data],
  );

  const rowVirtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 92, // Height of each row
    overscan: 5,
    onChange: (instance) => {
      const lastItem = instance.getVirtualItems().at(-1);
      if (
        lastItem &&
        lastItem.index >= messages.length - 5 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
  });

  return (
    <Selector>
      <SelectorSidebar>
        {isLabelsLoading ? (
          <div className="p-2 space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          <TreeView data={treeData} />
        )}
      </SelectorSidebar>
      <SelectorSearch
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder={`Search ${selectedTab.label
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b(\w)/g, (s) => s.toUpperCase())}...`}
        autoFocus
      />
      <SelectorResults ref={parentRef}>
        {isLoading ? (
          <div>
            {[1, 2, 3].map((i) => (
              <EmailCardSkeleton
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                key={i}
              />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            {error ? <p>Error loading messages</p> : <p>No messages found</p>}
          </div>
        ) : (
          <ErrorBoundary
            fallback={
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <p>Error loading messages</p>
              </div>
            }
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const message = messages[virtualRow.index];

                return (
                  <div
                    key={message.data.id}
                    style={{
                      overflow: "visible",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <EntityDragRow
                      entity={message as EmailEntity}
                      generic={genericById.get(message.data.id)}
                      entityId={message.data.id}
                      orderedIds={messageIds}
                      onSelect={(ent) => handleRecordSelect(ent as EmailEntity)}
                      dragScope="outlook"
                    >
                      <EmailCard
                        platform={message.platform}
                        {...message.data}
                        className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                      />
                    </EntityDragRow>
                  </div>
                );
              })}
            </div>
          </ErrorBoundary>
        )}
      </SelectorResults>
    </Selector>
  );
};
